import React from "react";
import { Phone, PhoneOff, Mic, MicOff, Lock, RefreshCw } from "lucide-react";
import CallHealthPanel from "../CallHealthPanel";
import { useI18n } from "../../i18n";

/**
 * iter71 CORTE 2 — Widget ÚNICO de llamada del dueño (CallCore UI).
 * Corte 4-D3 — Textos localizados vía i18n (idioma del propietario).
 *
 * Tabla estado→evento:
 *   connecting → owner_call.connecting   (evento: start() tras aceptar)
 *   connected  → owner_call.active + timer real (evento: pc ICE connected)
 *   failed     → owner_call.failed + Reintentar (CALL_CONNECT_TIMEOUT / ice_failed)
 *   ended      → owner_call.ended        (hangup de cualquier lado)
 *   idle       → owner_call.preparing    (aceptada, aún sin start)
 */
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const OwnerCallWidget = ({ callPhase, durationSec, muted, toggleMute, onHangup, onRetry, callError, health }) => {
  const { t } = useI18n();
  const inCall = callPhase === "connected";
  const connecting = callPhase === "connecting";
  const failed = callPhase === "failed";
  const ended = callPhase === "ended";

  const label = inCall ? t("owner_call.active")
    : connecting ? t("owner_call.connecting")
    : failed ? t("owner_call.failed")
    : ended ? t("owner_call.ended")
    : t("owner_call.preparing");

  return (
    <div className="m-4 rounded-2xl border border-emerald-400/30 bg-black/30 p-4" data-testid="owner-call-widget">
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-2 font-bold ${failed ? "text-red-300" : "text-emerald-300"}`}>
          <Phone className={`w-4 h-4 ${connecting ? "animate-pulse" : ""}`} />
          <span data-testid={`owner-call-state-${callPhase}`}>{label}</span>
        </div>
        {inCall && (
          <div className="text-sm text-emerald-200 font-mono font-bold" data-testid="owner-call-timer">
            {fmt(durationSec)}
          </div>
        )}
      </div>

      {failed && (
        <div className="mb-3 space-y-2">
          <p className="text-xs text-slate-300" data-testid="owner-call-failed-msg">
            {callError === "mic_denied"
              ? t("owner_call.mic_denied")
              : t("owner_call.failed_msg")}
          </p>
          <button
            onClick={onRetry}
            className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm flex items-center justify-center gap-2"
            data-testid="owner-call-retry-btn"
          >
            <RefreshCw className="w-4 h-4" /> {t("owner_call.retry")}
          </button>
        </div>
      )}

      {!failed && !ended && (
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm ${muted ? "bg-amber-500/20 text-amber-200" : "bg-white/10 text-slate-100"}`}
            data-testid="owner-call-mute-btn"
          >
            {muted ? <MicOff className="w-4 h-4 inline mr-1" /> : <Mic className="w-4 h-4 inline mr-1" />}
            {muted ? t("owner_call.muted") : t("owner_call.mute")}
          </button>
          <button
            onClick={onHangup}
            className="px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white font-bold text-sm"
            data-testid="owner-call-end-btn"
          >
            <PhoneOff className="w-4 h-4 inline mr-1" /> {t("owner_call.hangup")}
          </button>
        </div>
      )}

      <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
        <Lock className="w-3 h-3" /> {t("owner_call.encrypted")}
      </div>
      {(connecting || inCall) && <CallHealthPanel health={health} role="owner" />}
    </div>
  );
};

export default OwnerCallWidget;
